import { useField } from "formik";
import { useCallback, useDeferredValue, useLayoutEffect } from "react";

import type { ColorPickerInputProps } from "../../color";
import { ColorPickerInput } from "../../color";
import { useFieldContext } from "../FormFieldContext";
import { useCommitChange, useFieldLabelAndDescription, useFieldPlaceholder } from "./helpers";

type FieldColorPickerProps = Omit<ColorPickerInputProps, "value" | "onChange"> & {
  onChangeCommitted?: (value?: string, e?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

export const FieldColorPicker = ({ onChangeCommitted, ...props }: FieldColorPickerProps) => {
  const { controlId, name, dataTest } = useFieldContext();
  const [label, helperText] = useFieldLabelAndDescription(name);
  const fieldPlaceholder = useFieldPlaceholder(name);
  const [{ value }, _, { setValue, setError }] = useField<string>(name);
  const commitChange = useCommitChange();

  const colorValue = useDeferredValue(value);

  const handleChange = useCallback(
    (color: string) => {
      setValue(color).catch((error) => {
        setError(error);
        console.warn(error);
      });
    },
    [setError, setValue],
  );

  useLayoutEffect(() => {
    if (colorValue === value) return;

    commitChange();
    onChangeCommitted?.(value);
  }, [commitChange, onChangeCommitted, value, colorValue]);

  return (
    <ColorPickerInput
      id={controlId}
      name={name}
      value={value}
      label={label}
      helperText={helperText}
      placeholder={props.placeholder ?? fieldPlaceholder}
      {...props}
      onChange={handleChange}
      setError={setError}
      data-test={dataTest}
    />
  );
};
